import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useUI } from '../../context/UIContext';
import { Link } from 'react-router-dom';
import offlineManager from '../../utils/offlineManager';

export default function MobileDashboard() {
    const { user } = useAuth();
    const { socket } = useSocket();
    const { showToast } = useUI();
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [pendingOrders, setPendingOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const handleSync = async () => {
        if (!user?.companyId) return;
        setSyncing(true);
        try {
            await Promise.all([
                fetch(`/api/purchase-orders?companyId=${user.companyId}&limit=50`),
                fetch(`/api/products?companyId=${user.companyId}&limit=1000`),
                fetch(`/api/warehouses?companyId=${user.companyId}`),
                fetchOrders()
            ]);
            showToast("Synchronisation terminée : Données prêtes pour le hors-ligne", "success");
        } catch (err) {
            console.error("Sync failed", err);
            showToast("Erreur lors de la synchronisation", "error");
        } finally {
            setSyncing(false);
        }
    };

    const fetchOrders = useCallback(async () => {
        if (!user?.companyId) {
            setLoading(false);
            return;
        }
        try {
            const res = await fetch(`/api/purchase-orders?companyId=${user.companyId}&limit=50`);
            if (res.ok) {
                const data = await res.json();

                // Cache orders for search and offline use
                await offlineManager.saveOrders(data.orders);

                // Fetch Offline Receptions for filtering
                const queuedReceptions = await offlineManager.getQueuedReceptions();

                // Récupère les IDs des entrepôts accessibles à l'utilisateur
                const userWarehouseIds = (user.warehouses || []).map(w => w.id);
                const isAdmin = !userWarehouseIds.length;

                const pending = data.orders.map(o => {
                    // Pre-calculate offline status for each order
                    const pendingForThisOrder = queuedReceptions.filter(r => r.orderId === o.id);

                    // We now use the literals provided by the backend (totalQty, receivedQty)
                    // If not present (e.g. cached old version), we fallback to 0
                    const totalOrdered = Number(o.totalQty || 0);
                    const serverReceived = Number(o.receivedQty || 0);

                    // Sum all pending deltas in the local queue
                    const offlineDeltas = pendingForThisOrder.reduce((s, r) =>
                        s + (r.items || []).reduce((si, item) => si + (Number(item.delta) || 0), 0), 0
                    );

                    const currentTotalReceived = serverReceived + offlineDeltas;
                    const remaining = Math.max(0, totalOrdered - currentTotalReceived);
                    const progress = totalOrdered > 0 ? (currentTotalReceived / totalOrdered) * 100 : 0;

                    return {
                        ...o,
                        currentTotalReceived,
                        remaining,
                        progress,
                        isFullyReceivedLocally: currentTotalReceived >= totalOrdered && totalOrdered > 0,
                        isOverloaded: currentTotalReceived > totalOrdered
                    };
                }).filter(o => {
                    // 1. Basic Status Check: Now including 'Envoyée'
                    if (!['Envoyée', 'Livrée', 'Partiellement Livrée'].includes(o.status)) return false;

                    // 2. Hide if fully received locally
                    if (o.isFullyReceivedLocally) return false;

                    // 3. Permissions Check
                    if (isAdmin) return true;
                    if (!o.warehouseId) return true;
                    return userWarehouseIds.includes(o.warehouseId);
                });
                setPendingOrders(pending);
            }
        } catch (err) {
            console.error("Failed to fetch pending orders", err);
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, user?.warehouses]);


    useEffect(() => {
        if (user?.warehouses) {
            setWarehouses(user.warehouses);
            if (user.warehouses.length > 0) {
                setSelectedWarehouseId(prev => prev || user.warehouses[0].id.toString());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId]);


    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        if (!socket) return;

        socket.on('order_status_updated', fetchOrders);

        return () => {
            socket.off('order_status_updated', fetchOrders);
        };
    }, [socket, fetchOrders]);

    return (
        <div className="space-y-6">
            {/* Header Welcome */}
            <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-2xl">waving_hand</span>
                    </div>
                    <div>
                        <p className="text-sm text-slate-500">Bonjour,</p>
                        <p className="font-bold text-lg">{user?.name}</p>
                    </div>
                </div>
            </div>

            {/* Warehouse Selection */}
            <section className="space-y-3">
                <div className="flex justify-between items-center">
                    <h2 className="font-bold flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary text-sm">location_on</span>
                        Entrepôt Actif
                    </h2>
                </div>
                {warehouses.length === 0 ? (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-sm flex gap-3 items-start">
                        <span className="material-symbols-outlined shrink-0">warning</span>
                        <p>Vous n'êtes assigné à aucun entrepôt. Contactez votre administrateur.</p>
                    </div>
                ) : warehouses.length === 1 ? (
                    <div className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                <span className="material-symbols-outlined text-slate-500">warehouse</span>
                            </div>
                            <div>
                                <p className="font-bold text-slate-900 dark:text-white">{warehouses[0].name}</p>
                                <p className="text-xs text-slate-500">{warehouses[0].location || 'Localisation non définie'}</p>
                            </div>
                        </div>
                        <span className="material-symbols-outlined text-green-500 bg-green-50 dark:bg-green-900/20 p-1.5 rounded-full text-sm">check</span>
                    </div>
                ) : (
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-primary">warehouse</span>
                        <select
                            value={selectedWarehouseId}
                            onChange={(e) => setSelectedWarehouseId(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none font-bold shadow-sm"
                        >
                            {warehouses.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                        <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                    </div>
                )}
            </section>

            {/* Quick Actions */}
            <section>
                <Link
                    to="/mobile/scanner"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white p-6 rounded-2xl flex items-center justify-center gap-4 shadow-xl shadow-orange-500/30 transition-all active:scale-[0.98]"
                >
                    <span className="material-symbols-outlined text-4xl">barcode_scanner</span>
                    <span className="font-black text-lg uppercase tracking-wider">Lancer le Scanner</span>
                </Link>
            </section>

            {/* Pending Deliveries */}
            <section className="space-y-4">
                <h2 className="font-bold flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <span className="material-symbols-outlined text-primary text-sm">local_shipping</span>
                    Livraisons en attente
                </h2>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : pendingOrders.length === 0 ? (
                    <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 border-dashed dark:border-slate-700">
                        <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600 mb-2">package_2</span>
                        <p className="text-sm font-medium text-slate-500">Aucune commande en cours de livraison.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {pendingOrders.map(order => (
                            <Link
                                to={`/mobile/orders/${order.id}`}
                                key={order.id}
                                className="block p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary/50 rounded-xl shadow-sm transition-all"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-slate-900 dark:text-white">{order.reference}</span>
                                    <div className="flex gap-2">
                                        {order.isOverloaded && (
                                            <span className="text-[10px] font-black px-2 py-1 rounded bg-rose-500 text-white animate-pulse">
                                                SURPLUS
                                            </span>
                                        )}
                                        <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                            {order.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end text-[10px] font-bold uppercase tracking-tight text-slate-400">
                                        <span>Statut livraison</span>
                                        <span className="text-primary">{order.currentTotalReceived} reçus / {order.remaining} restants</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary rounded-full transition-all duration-500"
                                            style={{ width: `${order.progress}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
                                    <span className="flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[14px]">store</span>
                                        {order.supplier?.name || 'Fournisseur inconnu'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[14px]">calendar_today</span>
                                        {new Date(order.expectedDate).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
